import { Logger } from 'fsts';
const logger = new Logger('generalUtils');

type TextCase = 'lower' | 'upper' | 'both' | null;

// From https://github.com/mattphillips/deep-object-diff
export const isDate = (d: any) => d instanceof Date;
export const isEmpty = (o: any) => Object.keys(o).length === 0;
export const isObject = (o: any) => o != null && typeof o === 'object';
export const properObject = (o: any) => (isObject(o) && !o.hasOwnProperty ? { ...o } : o);
export const removeDuplicates = (array: any[]) => {
  return array.filter(function (item, pos, self) {
    return self.indexOf(item) == pos;
  });
};
export const deepClone = (o: any) => JSON.parse(JSON.stringify(o));

// Returns only the values that have been changed in the updated object
const updatedDiff = (lhs: any, rhs: any) => {
  if (lhs === rhs) return {};

  if (!isObject(lhs) || !isObject(rhs)) return rhs;

  const l = properObject(lhs);
  const r = properObject(rhs);

  if (isDate(l) || isDate(r)) {
    if (l.valueOf() == r.valueOf()) return {};
    return r;
  }

  return Object.keys(r).reduce((acc, key) => {
    // eslint-disable-next-line no-prototype-builtins
    if (l.hasOwnProperty(key)) {
      const difference: any = updatedDiff(l[key], r[key]);

      if (isObject(difference) && isEmpty(difference) && !isDate(difference)) return acc;

      return { ...acc, [key]: difference };
    }

    return acc;
  }, {});
};

// `debounce` purpose - Controls how often a function can execute. (for example when many times quickly change window size, approve only 1 request every 500 ms (can be 10-20 requests))
// debounce is a generic function that wraps another function (`func`)
// and ensures it is called only after a certain delay (`wait`)
// since the last call. It prevents rapid consecutive calls.
export function debounce<T extends (...args: any[]) => void>(
  func: T, // The function to debounce
  wait = 300 // The debounce delay in milliseconds (default: 300ms)
): (...args: Parameters<T>) => void {
  // Returns a new function with the same parameters as `func`

  // This variable will hold the ID of the timeout scheduled to call `func`
  let timeout: number | undefined;

  // Return a new function that wraps the original `func`
  return function (this: any, ...args: Parameters<T>) {
    // `this: any` allows `func` to access the correct context if used with `obj.method()`
    // `...args` collects all arguments passed to the wrapper function

    // If there's already a timeout scheduled, cancel it
    clearTimeout(timeout);

    // Schedule a new timeout to call `func` after `wait` milliseconds
    timeout = window.setTimeout(() => {
      // Call the original function with the correct `this` context and arguments
      func.apply(this, args);
    }, wait);
  };
}

/**
 * `Pauses execution` for a specified number of milliseconds.
 * Useful in async/await contexts to add delays, retries, or throttling. Returns a Promise that resolves after a specified delay
 * Enables async/await syntax for simple waiting without callbacks.
 *
 * @param ms - The number of milliseconds to wait.
 * @returns A Promise that resolves after the specified delay.
 *
 * @example
 * await delay(1000); // Waits 1 second before continuing
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    // setTimeout will call resolve after `ms` milliseconds
    setTimeout(resolve, ms);
  });
}

type QueueResult<T> = { result: T; isLast: boolean };

export default class GeneralUtils {
  public static isDevEnvironment(): boolean {
    return process.env.NODE_ENV == 'development';
  }
  public static isProdEnvironment(): boolean {
    return process.env.NODE_ENV == 'production';
  }
  public static isLocalDevEnv(): boolean {
    return window.location.host.toLowerCase().includes('localhost');
  }

  public static getObjProperty = (obj: any, prop: string) =>
    Object.entries(obj).find(([key]) => key.toLowerCase() === prop.toLowerCase())?.[1]; // would return pair ['key', 'value'] so need `1` index

  public static camelToSnakeCase(str: string) {
    let result = str
      .replace(/^[A-Z]/g, (letter) => `${letter.toLowerCase()}`)
      .replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    logger.debug(`camelToSnake:${str}->${result}`);
    return result;
  }
  public static maxint = 2147483647;
  public static updatedDiff = (lhs: any, rhs: any) => {
    return updatedDiff(lhs, rhs);
  };

  public static areObjectsEqual(lhs: any, rhs: any, ignoredProperties: Array<any> = []) {
    if (ignoredProperties.length > 0) {
      this.removeProperties(lhs, ignoredProperties);
      this.removeProperties(rhs, ignoredProperties);
    }

    let areObjectsEqual = areDeeplyEqual(lhs, rhs);
    let jsonEqual = JSON.stringify(lhs) === JSON.stringify(rhs);
    // console.log('object areobj:>> ', areObjectsEqual);
    return areObjectsEqual || jsonEqual;
  }

  public static removeProperties(obj: any, properties: any) {
    // no need for `return` since object passed by reference
    properties.forEach((el: any) => {
      if (obj && obj[el]) {
        delete obj[el];
      }
    });
  }

  // https://masteringjs.io/tutorials/fundamentals/wait-1-second-then
  public static delay(time: number) {
    if (time < 50) {
      time = time * 1000; // if less 50 guess it is seconds
    }
    return new Promise((resolve) => setTimeout(resolve, time));
  }

  public static generateRandomLetters(length: number = 3, textCase: TextCase = 'lower') {
    const lowerLetters = 'abcdefghijklmnopqrstuvwxyz';
    const upperLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

    let alphabet = lowerLetters; // Default is lowercase

    // Determine which character set to use based on the textCase parameter
    if (textCase === 'upper') {
      alphabet = upperLetters;
    } else if (textCase === null || textCase === 'both') {
      alphabet = lowerLetters + upperLetters; // Use both cases
    }

    let result = '';
    for (let i = 0; i < length; i++) {
      result += alphabet[Math.floor(Math.random() * alphabet.length)];
    }

    return result;
  }

  private static readonly UUID_V4_TEMPLATE = 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx';
  /**
   * Generates a Shopware-compatible UUID (32 hex chars, lowercase, no dashes).
   * Based on UUID v4 specification (RFC 4122).
   */
  public static generateShopwareUUID(): string {
    return this.UUID_V4_TEMPLATE.replace(/[xy]/g, (char: string): string => {
      // Generate a random 4-bit number (0–15) (`nibble` = half byte)
      const randomNibble = crypto.getRandomValues(new Uint8Array(1))[0] & 0x0f;

      // 'x' is fully random; 'y' must be 8–b (variant bits set to 10xx)
      // `nibble` == half-byte (4 bit)
      const nibble = char === 'x' ? randomNibble : (randomNibble & 0x03) | 0x08;

      return nibble.toString(16);
    });
  }

  /**
   * Splits an array into chunks of specified size
   * @param array - The array to chunk
   * @param chunkSize - The size of each chunk
   * @returns Array of chunks
   * @example
   * const ids = [1, 2, 3, 4, 5];
   * const batches = GeneralUtils.chunkArray(ids, 2);
   * // Result: [[1, 2], [3, 4], [5]]
   */
  public static chunkArray<T>(array: T[], chunkSize: number): T[][] {
    if (chunkSize <= 0) {
      throw new Error('Chunk size must be greater than 0');
    }

    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  private static queueCounter = 0;
  private static executionQueue: Promise<void> = Promise.resolve();
  // Track if the queue is currently processing items
  private static isQueueActive = false;

  public static queueCall<T>(
    func: (isLast: boolean) => Promise<T>,
    delayMs: number = 300,
    initialWaitDelayMs: number = 1500
  ): Promise<QueueResult<T>> {
    const myOrder = ++this.queueCounter;

    let resolveFn!: (value: QueueResult<T>) => void;
    let rejectFn!: (reason?: any) => void;

    const myPromise = new Promise<QueueResult<T>>((resolve, reject) => {
      resolveFn = resolve;
      rejectFn = reject;
    });

    // Determine the "thinking buffer" before entering the promise chain
    // If not active, it's the 1st click: wait 500ms. If active, wait 50ms.
    const waitTime = this.isQueueActive ? 50 : initialWaitDelayMs;
    this.isQueueActive = true;

    this.executionQueue = this.executionQueue.then(async () => {
      // "Time to think": Wait for the current execution stack to clear.
      // This allows clicks 2, 3, and 4 to increment 'this.queueCounter'
      // before Click 1 performs its 'isLast' check.
      await new Promise((resolve) => setTimeout(resolve, waitTime));

      try {
        const isLast = myOrder === this.queueCounter;

        // console.log(`🔗 Executing #${myOrder}: isLast=${isLast}, queueCounter=${this.queueCounter}`);
        const result = await func(isLast);

        await new Promise((r) => setTimeout(r, delayMs));

        // Re-calculate isLast for the return result,
        // in case a 5th click happened while func() was running
        const strictlyLast = myOrder === this.queueCounter;

        // Reset the flag so the next separate interaction gets the long buffer
        if (strictlyLast) {
          this.isQueueActive = false;
        }

        resolveFn({ result, isLast: strictlyLast });
      } catch (err) {
        console.error('Queued function failed:', err);

        resolveFn({
          result: null as unknown as T,
          isLast: myOrder === this.queueCounter,
        });
      }
    });

    return myPromise;
  }

  //Reset counters (e.g., on page navigation)
  public static resetQueueCounters() {
    this.queueCounter = 0;
    this.executionQueue = Promise.resolve();
  }

  /**
   * Creates a debounced version of a function that delays its execution
   * until after `wait` milliseconds have elapsed since the last time it was invoked.
   *
   *   Use case: Prevent excessive API calls when user rapidly selects/deselects options.
   *    Example: User clicks 7 roles in 2 seconds → only 1 API call after 5s of inactivity.
   *
   * ⚠️ Note:
   *   - DON'T forget to `cancel` in `beforeDestroy`
   *   - The debounced function returns `void` (fire-and-forget).
   *   - If you need the result, wrap `func` to handle promises internally.
   *   - Does NOT support immediate execution (leading edge) or maxWait – keep it simple!
   *
   * @template T - Function type with any arguments and any return type
   * @param func - The function to debounce (can be async)
   * @param wait - Delay in milliseconds to wait after last invocation before executing
   * @returns A new function that, when called, resets the debounce timer
   *
   * @example
   * const saveChanges = GeneralUtils.debounce(async (data) => {
   *   await api.save(data);
   * }, 5000);
   *
   * // Call multiple times rapidly:
   * saveChanges({role: 1}); // timer starts
   * saveChanges({role: 2}); // timer resets
   * saveChanges({role: 3}); // timer resets again //⚠️ Note: Only the last call's arguments are used (if logic for each step is important it WILL NOT implemented as part of this debounce function, you need to handle it separately)
   * // After 5s of no calls → executes once with {role: 3}
   */
  public static debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): ((...args: Parameters<T>) => void) & { cancel: () => void } {
    // Holds the timeout ID so we can clear/reset it on subsequent calls
    let timeout: ReturnType<typeof setTimeout> | null = null;

    //  Define the wrapper function
    const debounced = (...args: Parameters<T>): void => {
      // This function executes after the wait period
      const later = () => {
        timeout = null; // Clear reference to allow future calls
        func(...args); // Execute original function with saved arguments
      };
      // If a timer is already running, cancel it (reset the debounce window)
      if (timeout !== null) {
        clearTimeout(timeout);
      }
      // Start a new timer
      timeout = setTimeout(later, wait);
    };

    // Attach the cancel method to the function object
    debounced.cancel = () => {
      if (timeout !== null) {
        clearTimeout(timeout);
        timeout = null; // Ensure the timer is fully cleared
      }
    };

    // Return the debounced wrapper function
    return debounced;
  }
}

function areDeeplyEqual(obj1: any, obj2: any): any {
  // fix console error
  if (obj1 === undefined && obj2 === undefined) {
    return true;
  }
  if (obj1 === null && obj2 === null) {
    return true;
  }
  if (obj1 === undefined || obj2 === undefined) {
    return false;
  }
  if (obj1 === null || obj2 === null) {
    return false;
  }

  //Loop through properties in object 1
  for (var p in obj1) {
    //Check property exists on both objects
    if (obj1.hasOwnProperty(p) !== obj2.hasOwnProperty(p)) return false;

    switch (typeof obj1[p]) {
      //Deep compare objects
      case 'object':
        if (!areDeeplyEqual(obj1[p], obj2[p])) return false;
        break;
      //Compare function code
      case 'function':
        if (typeof obj2[p] == 'undefined' || (p != 'compare' && obj1[p].toString() != obj2[p].toString())) return false;
        break;
      //Compare values
      default:
        if (obj1[p] != obj2[p]) return false;
    }
  }

  //Check object 2 for any extra properties
  for (var p in obj2) {
    if (typeof obj1[p] == 'undefined') return false;
  }
  return true;
}
